import React, { useState, useEffect, useRef } from 'react';
import { X, LogOut } from 'lucide-react';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { auth$, getListMembers, addMember, removeMember, findUserByEmail } from '../store';
import { useScrollLock } from '../hooks/useScrollLock';
import UserAvatar from './UserAvatar';
import { Dialog, ICON_SIZE } from '../design-system';
import './ListMembers.css';

// Stacked avatars display (no click handler - just display)
export const MemberAvatars = ({ listId }) => {
  const [members, setMembers] = useState([]);
  useEffect(() => { if (listId) getListMembers(listId).then(setMembers); }, [listId]);
  if (members.length <= 1) return null;
  const displayMembers = members.slice(0, 2);
  const remaining = members.length - 2;
  return (
    <div className="member-avatars-stack">
      {displayMembers.map((m, i) => (
        <div key={m.id} className="avatar-wrapper" style={{ zIndex: 10 - i, marginLeft: i > 0 ? -8 : 0 }}>
          <UserAvatar
            userId={m.id}
            username={m.username || m.email}
            avatarUrl={m.avatar_url}
            size={24}
            className="member-avatar"
          />
        </div>
      ))}
      {remaining > 0 && <div className="avatar-wrapper remaining" style={{ marginLeft: -8 }}>+{remaining}</div>}
    </div>
  );
};

// Invite member modal (centered overlay)
export const InviteModal = ({ listId, isOpen, onClose, onMembersChange }) => {
  const { t } = useTranslation('modals');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Lock scroll when modal is open
  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setEmail(''); setError(''); setSuccess('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen || !listId) return null;

  const handleInvite = async () => {
    if (!email.trim()) return;
    setError(''); setSuccess(''); setLoading(true);
    try {
      const user = await findUserByEmail(email.trim());
      if (!user) { setError(t('invite.userNotFound')); setLoading(false); return; }
      const result = await addMember(listId, user.id);
      if (!result.success) { setError(result.error); setLoading(false); return; }
      setSuccess(t('invite.invited', { username: user.username }));
      setEmail('');
      onMembersChange?.();
      setTimeout(() => onClose(), 1500);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      ariaLabel={t('invite.title')}
      showClose={false}
      className="list-members-modal"
    >
      <div className="list-members-header">
        <span>{t('invite.title')}</span>
        <button onClick={onClose}><X size={ICON_SIZE.xs} /></button>
      </div>
      <div className="list-members-body">
        <input
          ref={inputRef}
          type="email"
          placeholder={t('invite.placeholder')}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
          disabled={loading}
        />
        <button className="invite-submit-btn" onClick={handleInvite} disabled={loading || !email.trim()}>
          {loading ? t('invite.inviting') : t('invite.invite')}
        </button>
        {error && <div className="invite-error">{error}</div>}
        {success && <div className="invite-success">{success}</div>}
      </div>
    </Dialog>
  );
};

// Members panel modal (centered overlay)
export const MembersPanel = ({ listId, isOpen, onClose, onLeave, onMembersChange }) => {
  const { t } = useTranslation('modals');
  const [members, setMembers] = useState([]);
  const currentUserId = useSelector(() => auth$.user.get()?.id);
  const isOwner = members.find(m => m.user_id === currentUserId)?.role === 'owner';

  // Lock scroll when panel is open
  useScrollLock(isOpen);

  const loadMembers = async () => {
    if (listId) {
      const data = await getListMembers(listId);
      setMembers(data);
    }
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-open: members load when the panel opens (loadMembers sets state after await)
  useEffect(() => { if (isOpen && listId) loadMembers(); }, [isOpen, listId]);

  if (!isOpen || !listId) return null;

  const handleRemove = async (userId) => {
    await removeMember(listId, userId);
    loadMembers();
    onMembersChange?.();
  };

  const handleLeave = async () => {
    if (!confirm(t('members.leaveConfirm'))) return;
    await removeMember(listId, currentUserId);
    onClose();
    onLeave?.();
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      ariaLabel={t('members.title')}
      showClose={false}
      className="list-members-modal members-modal"
    >
      <div className="list-members-header">
        <span>{t('members.title')}</span>
        <button onClick={onClose}><X size={ICON_SIZE.xs} /></button>
      </div>
      <div className="members-list">
        {members.map(m => (
          <div key={m.id} className="member-row">
            <UserAvatar
              userId={m.id}
              username={m.username || m.email}
              avatarUrl={m.avatar_url}
              size={28}
              className="member-avatar"
            />
            <div className="member-info">
              <span className="member-name">{m.username}</span>
              {m.role === 'owner' && <span className="member-role">{t('members.owner')}</span>}
            </div>
            {isOwner && m.user_id !== currentUserId && (
              <button className="remove-btn" onClick={() => handleRemove(m.user_id)}><X size={ICON_SIZE.xxs} /></button>
            )}
          </div>
        ))}
      </div>
      {!isOwner && (
        <button className="leave-btn" onClick={handleLeave}>
          <LogOut size={ICON_SIZE.xs} /> {t('members.leaveList')}
        </button>
      )}
    </Dialog>
  );
};

export default MemberAvatars;

